import { useState } from "react";
import ErrorMessage from "components/atoms/forms/ErrorMessage";
import { toast } from "react-toastify";
import MediaUploadWithLabel from "components/molecules/forms/MediaUploadWithLabel";
import Button from "components/atoms/buttons/Button";
import ChoiceInputElement from "components/organisms/merchdrop/ChoiceInputElement";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { ShareIcon } from "components/atoms/Icon";
import { useFieldArray, useFormContext } from "react-hook-form";
import CheckboxWithLabel from "components/molecules/forms/CheckboxWithLabel";
import HintIconWithTooltip from "components/molecules/forms/HintIconWithTooltip";
import { TOOLTIP_HINT_TEXT } from "constants/constants";
import TextAreaWithLabel from "components/molecules/forms/TextAreaWithLabel";
import Label from "components/atoms/forms/Label";
import InputFieldWithLabel from "components/molecules/forms/InputFieldWithLabel";
// @ts-ignore
import SampleCSV from "assets/claim_codes.csv";
import { TOAST_CONFIG } from "../../constants";

function LabelRender() {
  return (
    <>
      Upload Media (optional)
      <HintIconWithTooltip
        hintText={
          <div className="max-w-[21rem] space-y-1 py-0.5 text-base leading-snug">
            <p>Currently Supported file types and sizes:</p>
            <p>
              <span className="font-bold">1. Image</span>: JPG, PNG, BMP, GIF, WEBP | Size 5 KB -10 MB
            </p>
            <p>
              <span className="font-bold">2. Video</span>: MP4 | Size 1 MB - 500 MB
            </p>
            <p>
              <span className="font-bold">3. Audio</span>: MP3 | Size 50 KB - 100 MB
            </p>
            <p>
              <span className="font-bold">4. Document</span>: PDF, ZIP | Size 1 KB - 50 MB
            </p>
          </div>
        }
        direction="bottom"
      />
    </>
  );
}

export default function MementoSpecificFields({ activationKey }: { activationKey: ActivationsKeys }) {
  const {
    register,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    setError,
    control,
  } = useFormContext<MementoFormType>();
  const [resetMediaUploadCount, setResetMediaUploadCount] = useState(0);
  const { fields: merchDropOptions, append, remove } = useFieldArray({ control, name: "options" });

  const handleClearMediaFile = () => {
    if (watch("media") || watch("media_url_new")) {
      setValue("media", undefined);
      setValue("media_url_new", "");
      setValue("media_type", "");
      clearErrors(["media", "media_url_new"]);
    } else if (watch("media_url")) {
      setValue("media_url", "");
      clearErrors(["media"]);
    }
    setResetMediaUploadCount(resetMediaUploadCount + 1);
  };

  const onMediaUploadSuccess = (mediaURL: string) => {
    setValue("media_url_new", mediaURL);
    setValue("media", undefined);
  };

  const onMediaUploadError = (error: any) => {
    setValue("media_url_new", undefined);
    setError("media", { message: error.message });
  };

  const handleSetFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;

    if (!files?.length || !files) {
      return toast.error("No file was selected..", TOAST_CONFIG);
    }

    setValue("media", files[0], {
      shouldValidate: true,
    });

    return null;
  };

  const handleRemoveOption = (optionIndexToRemove: number) => {
    remove(optionIndexToRemove);
  };

  const handleAddOption = () => {
    append({ name: "", choices: [] });
  };

  const handleErrorMessage = () => {
    let errorMessage = null;

    if (errors.media) {
      errorMessage = `${errors.media?.message} Please checkout the ' ? ' icon for details.`;
    }
    if (errors.media_url) {
      errorMessage = `${errors.media_url?.message} Please checkout the ' ? ' icon for details.`;
    }
    return errorMessage;
  };

  switch (activationKey) {
    case "mem_drop":
      return (
        <div>
          <MediaUploadWithLabel
            label={<LabelRender />}
            placeHolder="Click to select a file"
            setMediaFile={(e) => {
              const selectedFile = e.target.files?.item(0);

              // Check if selectedFile is not null or undefined
              if (selectedFile) {
                // Continue with setting the media file if it passes the type and size checks
                setValue("media_type", selectedFile.type || "");
                setValue("media", selectedFile, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
              }
            }}
            id="media"
            errorMessage={handleErrorMessage()}
            selectedFile={watch("media")}
            clearSelection={handleClearMediaFile}
            onMediaUploadSuccess={onMediaUploadSuccess}
            onMediaUploadError={onMediaUploadError}
            mediaURL={watch("media_url_new") || watch("media_url") || undefined}
            key={resetMediaUploadCount}
          />
          <div className="flex items-center">
            <CheckboxWithLabel
              isDisabled={watch("is_incentive")}
              isChecked={watch("is_gated")}
              label="Exclusive Content"
              id="is_gated"
              register={register}
            />
            <HintIconWithTooltip
              direction="right"
              hintTextClassName="w-72"
              hintText={TOOLTIP_HINT_TEXT.GatedContentCheckboxTooltipText}
            />
          </div>
        </div>
      );

    case "claim_code":
      return (
        <div className="">
          <div className="">
            <TextAreaWithLabel
              label="Claim Code Instruction"
              labelClasses="text-xxs 2xl:text-xs"
              id="claim_instructions"
              rows={7}
              placeHolder="Enter the instructions of the Claim Code."
              register={register}
              defaultValue=""
              descriptionWordCount={watch("claim_instructions")?.length || 0}
              errorMessage={errors.claim_instructions?.message}
              maxDescriptionWordCount={1000}
            />
          </div>
          <div className="flex gap-4">
            <div className="">
              <div className="flex">
                <div>
                  <Label htmlFor="media" label="Upload Claim Codes" />
                  <div className="flex items-center">
                    <label htmlFor="media" className="relative flex cursor-pointer">
                      <div
                        className="mt-2 inline-flex w-64 items-center justify-center
                          space-x-2 rounded-full border-1.5 border-white
                         p-2 font-sora text-xxs font-semibold"
                      >
                        <ShareIcon className="h-7 w-7 text-white  2xl:h-6 2xl:w-6" />
                        <p className="text-white">
                          {watch("media") || watch("media_url") ? "CHANGE CSV" : "UPLOAD CSV"}
                        </p>
                      </div>
                      <input
                        id="media"
                        accept=".csv"
                        name="media"
                        type="file"
                        className="sr-only"
                        onChange={handleSetFile}
                      />
                    </label>
                  </div>
                  <div className="mt-2">
                    {watch("media") ? (
                      <div className="ml-2 flex items-center font-satoshi font-medium text-white">
                        {watch("media")?.name}
                      </div>
                    ) : (
                      watch("media_url") && (
                        <div className="ml-2 flex items-center">
                          <a className="text-white" href={watch("media_url")}>
                            Uploaded File
                          </a>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
              <ErrorMessage errorMessage={errors.media?.message || errors.file?.message} />
            </div>
            <div className="">
              <Label htmlFor="Download Codes Template" label="Download Codes Template" />

              <a
                href={SampleCSV}
                className="mt-2 inline-flex w-64 items-center justify-center
                     space-x-2 rounded-full border-1.5 border-white
                    p-2 font-sora text-xxs font-semibold"
                download="Sample File"
              >
                <ShareIcon className="h-7 w-7 rotate-180 text-white  2xl:h-6 2xl:w-6" />
                <p className="text-white">DOWNLOAD TEMPLATE</p>
              </a>
            </div>
          </div>
        </div>
      );
    case "merch_drop":
      return (
        <div className="">
          <div className="mb-1">
            <p className="font-sora text-xs font-medium text-white">Merchandise Options (Max 3) *</p>
          </div>
          <div className="space-y-4">
            {merchDropOptions.map((option, idx) => (
              <div
                className="relative grid w-full grid-cols-12 gap-x-2 rounded-2xl border border-white bg-primary p-4"
                key={option.id}
              >
                <button
                  type="button"
                  disabled={idx < 1}
                  className="absolute right-0 z-30  m-3 opacity-70 transition-all duration-100 hover:scale-110 hover:opacity-100 disabled:hidden "
                  onClick={() => handleRemoveOption(idx)}
                >
                  <XMarkIcon className="h-5 w-5 text-white" />
                </button>
                <div className="col-span-12">
                  <InputFieldWithLabel
                    label="Option Name"
                    id={`options.${idx}.name`}
                    register={register}
                    placeHolder="Enter the Option Name. Eg Color"
                    additionalClasses="border-opacity-20"
                    defaultValue=""
                    errorMessage={errors.options?.[idx]?.name?.message}
                  />
                </div>
                <div className="col-span-12">
                  <ChoiceInputElement
                    choices={watch(`options.${idx}.choices`)}
                    setChoices={setValue}
                    id={`options.${idx}.choices`}
                    additionClasses="border-opacity-20"
                    inputAdditionalClasses={watch(`options.${idx}.choices`).length > 0 ? "" : "w-full"}
                    label="Choices"
                    placeHolder={
                      watch(`options.${idx}.choices`).length > 0 ? "" : "Enter the Choices Eg. Red, White, etc."
                    }
                    error={errors.options?.[idx]?.choices?.message}
                    setError={setError}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="my-3 flex justify-center">
            <Button
              text="+ Add Option"
              disabled={merchDropOptions.length > 2}
              variant="rustPinkSolid"
              additionalClasses="!font-medium disabled:hidden"
              onClick={handleAddOption}
            />
          </div>
        </div>
      );
    default:
      return null;
  }
}
